import { setup, assign } from 'xstate';

export interface JobPostingContext {
  title: string;
  description: string;
  requirements: string[];
  skills: string[];
  province: string;
  city: string;
  salaryMin: string;
  salaryMax: string;
  workType: string;
  applicationDeadline: string;
  draftSaved: boolean;
  publishError: string | null;
}

export const emptyJobPostingContext: JobPostingContext = {
  title: '',
  description: '',
  requirements: [],
  skills: [],
  province: '',
  city: '',
  salaryMin: '',
  salaryMax: '',
  workType: 'Contract',
  applicationDeadline: '',
  draftSaved: false,
  publishError: null,
};

export type JobPostingEvent =
  | { type: 'START' }
  | { type: 'SUBMIT_DETAILS'; title: string; description: string; requirements: string[] }
  | { type: 'SUBMIT_SKILLS'; skills: string[] }
  | { type: 'SUBMIT_LOCATION'; province: string; city: string }
  | { type: 'SUBMIT_COMPENSATION'; salaryMin: string; salaryMax: string; workType: string; applicationDeadline: string }
  | { type: 'REVIEW' }
  | { type: 'EDIT_STEP'; step: string }
  | { type: 'PUBLISH' }
  | { type: 'SAVE_DRAFT' }
  | { type: 'PUBLISHED' }
  | { type: 'PUBLISH_ERROR'; message: string }
  | { type: 'BACK' };

export const jobPostingMachine = setup({
  types: {
    context: {} as JobPostingContext,
    events: {} as JobPostingEvent,
  },
  guards: {
    hasSkills: ({ context }) => context.skills.length > 0,
  },
}).createMachine({
  id: 'jobPosting',
  context: emptyJobPostingContext,
  initial: 'idle',
  states: {
    idle: { on: { START: 'jobDetails' } },
    jobDetails: {
      on: {
        SUBMIT_DETAILS: {
          target: 'skills',
          actions: assign({
            title: ({ event }) => event.title,
            description: ({ event }) => event.description,
            requirements: ({ event }) => event.requirements,
          }),
        },
        SAVE_DRAFT: { actions: assign({ draftSaved: true }) },
        BACK: 'idle',
      },
    },
    skills: {
      on: {
        SUBMIT_SKILLS: {
          target: 'location',
          actions: assign({ skills: ({ event }) => event.skills }),
        },
        SAVE_DRAFT: { actions: assign({ draftSaved: true }) },
        BACK: 'jobDetails',
      },
    },
    location: {
      on: {
        SUBMIT_LOCATION: {
          target: 'compensation',
          actions: assign({
            province: ({ event }) => event.province,
            city: ({ event }) => event.city,
          }),
        },
        SAVE_DRAFT: { actions: assign({ draftSaved: true }) },
        BACK: 'skills',
      },
    },
    compensation: {
      on: {
        SUBMIT_COMPENSATION: {
          target: 'review',
          actions: assign({
            salaryMin: ({ event }) => event.salaryMin,
            salaryMax: ({ event }) => event.salaryMax,
            workType: ({ event }) => event.workType,
            applicationDeadline: ({ event }) => event.applicationDeadline,
          }),
        },
        SAVE_DRAFT: { actions: assign({ draftSaved: true }) },
        BACK: 'location',
      },
    },
    review: {
      on: {
        EDIT_STEP: [
          { target: 'jobDetails', guard: ({ event }) => event.step === 'details' },
          { target: 'skills', guard: ({ event }) => event.step === 'skills' },
          { target: 'location', guard: ({ event }) => event.step === 'location' },
          { target: 'compensation', guard: ({ event }) => event.step === 'compensation' },
        ],
        PUBLISH: 'publishing',
        SAVE_DRAFT: { actions: assign({ draftSaved: true }) },
        BACK: 'compensation',
      },
    },
    publishing: {
      on: {
        PUBLISHED: 'published',
        PUBLISH_ERROR: {
          target: 'review',
          actions: assign({ publishError: ({ event }) => event.message }),
        },
      },
    },
    published: { type: 'final' },
  },
});

export const jobPostingSteps = ['jobDetails', 'skills', 'location', 'compensation', 'review'] as const;
